const SEPARATOR = '__';

interface CollapsedValue {
  label: string;
  originalValues: string[];
}

/**
 * Collapses tree select values that share a common parent into a single
 * entry with the parent's label. When a parent node absorbs its children,
 * all deeper descendants under that parent are also absorbed.
 *
 * Example: selecting "Cardiovascular" group stores all leaves:
 *   ['Cardiovascular__Hypertension', 'Cardiovascular__Vasculitis',
 *    'Cardiovascular__Vasculitis__Systemic Vasculitis',
 *    'Cardiovascular__Vasculitis__Systemic Vasculitis__Granulomatosis With Polyangiitis']
 * → collapses to one entry: { label: 'Cardiovascular', originalValues: [...all above] }
 */
export const collapseTreeSelectValues = (values: string[]): CollapsedValue[] => {
  if (values.length <= 1) {
    return values.map(value => ({
      label: getLeaf(value),
      originalValues: [value],
    }));
  }

  let paths = [...values];
  let changed = true;

  while (changed) {
    changed = false;
    const byParent = new Map<string, string[]>();

    for (const path of paths) {
      const sepIdx = path.lastIndexOf(SEPARATOR);
      const parent = sepIdx === -1 ? '' : path.slice(0, sepIdx);
      const group = byParent.get(parent);
      if (group) {
        group.push(path);
      } else {
        byParent.set(parent, [path]);
      }
    }

    const collapsed = new Set<string>();
    const next: string[] = [];

    for (const [parent, children] of byParent) {
      if (children.length > 1 && parent !== '') {
        next.push(parent);
        collapsed.add(parent);
        changed = true;
      }
    }

    for (const [, children] of byParent) {
      for (const child of children) {
        if (isAbsorbedBy(child, collapsed)) continue;
        if (!collapsed.has(child)) next.push(child);
      }
    }

    paths = next;
  }

  return paths.map(path => ({
    label: getLeaf(path),
    originalValues: values.filter(original => original === path || original.startsWith(path + SEPARATOR)),
  }));
};

const getLeaf = (value: string): string => {
  const lastSep = value.lastIndexOf(SEPARATOR);
  return lastSep === -1 ? value : value.slice(lastSep + SEPARATOR.length);
};

/** Returns true if `path` is a descendant of any entry in `ancestors`. */
const isAbsorbedBy = (path: string, ancestors: Set<string>): boolean => {
  for (const ancestor of ancestors) {
    if (path === ancestor || path.startsWith(ancestor + SEPARATOR)) return true;
  }
  return false;
};
